import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type HubInfo, type ProjectCard } from '@/api/client';
import { useAuth } from '@/auth/store';
import { useDocumentTitle } from '@/compliance/ResearchUse';
import { formatDate } from './format';
import { RunPipelineDialog } from './RunPipelineDialog';

type SortKey = 'date' | 'name';
type ViewMode = 'grid' | 'list';

export default function GalleryPage() {
  useDocumentTitle('Valvia - Gallery');
  const navigate = useNavigate();
  const isAdmin = useAuth((s) => s.me?.user.role === 'admin');
  const [projects, setProjects] = useState<ProjectCard[] | null>(null);
  const [hub, setHub] = useState<HubInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>(() => (localStorage.getItem('gallery.sort') as SortKey) || 'date');
  const [view, setView] = useState<ViewMode>(() => (localStorage.getItem('gallery.view') as ViewMode) || 'grid');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api
      .get<{ projects: ProjectCard[] }>('/api/projects')
      .then((r) => setProjects(r.projects))
      .catch((e) => setError((e as Error).message));
  }, []);
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<HubInfo>('/api/hub')
      .then(setHub)
      .catch(() => setHub(null));
  }, [isAdmin]);
  useEffect(() => localStorage.setItem('gallery.sort', sort), [sort]);
  useEffect(() => localStorage.setItem('gallery.view', view), [view]);

  const shown = useMemo(() => {
    if (!projects) return [];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? projects.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.notes.toLowerCase().includes(q) ||
            p.pipeline_versions.some((v) => v.toLowerCase().includes(q)),
        )
      : projects.slice();
    filtered.sort((a, b) =>
      sort === 'name'
        ? a.name.localeCompare(b.name)
        : (b.last_run_at ?? b.created_at).localeCompare(a.last_run_at ?? a.created_at),
    );
    return filtered;
  }, [projects, query, sort]);

  const canCreate = isAdmin && hub?.configured;

  return (
    <div className="page">
      <div className="toolbar">
        <h1>Projects</h1>
        <input
          type="search"
          placeholder="Search name, notes, version"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label>
          Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="date">Newest run</option>
            <option value="name">Name</option>
          </select>
        </label>
        <div className="segmented">
          <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')}>
            Grid
          </button>
          <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>
            List
          </button>
        </div>
        <span className="spacer" />
        {canCreate && (
          <button onClick={() => setCreating(true)} title={hub?.online ? 'Upload a scan and run the pipeline' : 'The pipeline hub is not reachable'}>
            New project
          </button>
        )}
      </div>
      {error && <div className="error">{error}</div>}
      {projects === null && !error && <div className="muted">Loading…</div>}
      {projects && projects.length === 0 && (
        <div className="empty">
          No projects yet.{' '}
          {canCreate ? (
            <>
              Create one from a NIfTI or DICOM scan with <b>New project</b>, or add converted runs with <code>valvia-web ingest</code>.
            </>
          ) : (
            <>
              Runs are added with <code>valvia-web ingest</code>.
            </>
          )}
        </div>
      )}
      {view === 'grid' ? (
        <div className="card-grid">
          {shown.map((p) => (
            <ProjectCardView key={p.id} project={p} />
          ))}
        </div>
      ) : (
        <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Last run</th>
              <th>Runs</th>
              <th>Pipeline</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id}>
                <td data-label="Name" className="span">
                  <Link to={`/projects/${p.id}`}>{p.name}</Link>
                </td>
                <td data-label="Last run">{formatDate(p.last_run_at)}</td>
                <td data-label="Runs">{p.run_count}</td>
                <td data-label="Pipeline">{p.pipeline_versions.map((v) => <span key={v} className="badge">{v}</span>)}</td>
                <td data-label="Notes">{p.has_notes ? <span className="badge notes">notes</span> : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {creating && hub && (
        <RunPipelineDialog
          hub={hub}
          onClose={() => setCreating(false)}
          onSubmitted={(projectId) => {
            setCreating(false);
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
    </div>
  );
}

function ProjectCardView({ project: p }: { project: ProjectCard }) {
  const navigate = useNavigate();
  return (
    <Link to={`/projects/${p.id}`} className="card">
      <div className="thumb">
        {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" loading="lazy" /> : <div className="thumb-empty">no preview</div>}
      </div>
      <div className="card-body">
        <div className="card-head">
          <div className="card-title">{p.name}</div>
          {p.last_run_id && (
            <button
              className="card-load"
              title="Open the latest run in the viewer"
              onClick={(e) => {
                // The card itself links to the project page; this opens the run directly.
                e.preventDefault();
                e.stopPropagation();
                navigate(`/runs/${p.last_run_id}`);
              }}
            >
              Load
            </button>
          )}
        </div>
        <div className="muted small">{formatDate(p.last_run_at)}</div>
        <div className="badges">
          {p.pipeline_versions.map((v) => (
            <span key={v} className="badge">
              {v}
            </span>
          ))}
          {p.has_notes && <span className="badge notes">notes</span>}
          {p.run_count > 1 && <span className="badge">{p.run_count} runs</span>}
          {p.run_count === 0 && <span className="badge">no runs</span>}
        </div>
      </div>
    </Link>
  );
}
